import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import jwtDecode from "jwt-decode";
import { notifyAuthSessionUpdated } from "../utils/authSession";

const AdminPrivateRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 15; // Try up to 15 times (3 seconds total)
    
    // First, check if there's a token in the URL and process it
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get("token");
    
    if (tokenFromUrl) {
      try {
        // Process token immediately
        localStorage.setItem("sundorbonToken", tokenFromUrl);
        localStorage.setItem("token", tokenFromUrl);
        
        const decoded = jwtDecode(tokenFromUrl);
        const userDetails = {
          Id: decoded.UserId,
          Name: decoded.Username || decoded.UserName,
          Type: decoded.RoleName || decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"],
          Email: decoded.sub || decoded.UserEmail,
        };
        localStorage.setItem(
          "sundorbonBookingUserDetails",
          JSON.stringify(userDetails)
        );
        notifyAuthSessionUpdated();

        // Clean up token from URL
        queryParams.delete("token");
        const newSearch = queryParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        navigate(newUrl, { replace: true });
      } catch (error) {
        console.error("Error processing token:", error);
      }
    }
    
    const checkAuthorization = () => {
      const localdata = localStorage.getItem("sundorbonBookingUserDetails");
      
      // If no data and we haven't exhausted retries, try again
      if (!localdata && retryCount < maxRetries) {
        retryCount++;
        setTimeout(checkAuthorization, 200); // Wait 200ms and retry
        return;
      }
      
      if (!localdata) {
        console.warn("AdminPrivateRoute - No user details found after retries");
        toast.error("Please login to access this page.", {
          position: "top-right",
          autoClose: 3000,
        });
        navigate("/");
        setIsChecking(false);
        return;
      }

      try {
        const localStorageObj = JSON.parse(localdata);
        const userRole = localStorageObj?.Type;
        
        // Debug logging
        console.log("AdminPrivateRoute - User Role:", userRole);
        console.log("AdminPrivateRoute - Full user details:", localStorageObj);

        // Check if role is Admin (case-insensitive)
        const normalizedRole = userRole ? userRole.toString().trim() : "";
        if (normalizedRole.toLowerCase() === "admin" || normalizedRole === "Admin") {
          console.log("AdminPrivateRoute - Authorization granted");
          setIsAuthorized(true);
        } else {
          console.warn("AdminPrivateRoute - Access denied. User role:", userRole, "Expected: Admin");
          toast.error(`Access Denied. Admin role required. Current role: ${userRole || "None"}`, {
            position: "top-right",
            autoClose: 5000,
          });
          navigate("/");
        }
      } catch (error) {
        console.error("Error parsing user details:", error);
        toast.error("Session expired. Please login again.", {
          position: "top-right",
          autoClose: 3000,
        });
        navigate("/");
      } finally {
        setIsChecking(false);
      }
    };

    // Start checking immediately, then retry if needed
    checkAuthorization();
  }, [navigate, location.search, location.pathname]);

  if (isChecking) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Checking authorization...</div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="flex justify-center items-center min-h-screen flex-col gap-3">
        <h1 className="text-4xl font-semibold text-red-600">Access Denied</h1>
        <p className="text-xl text-gray-600">You do not have permission to access this page.</p>
        <p className="text-lg text-gray-500">Admin role required.</p>
        <button 
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" 
          onClick={() => navigate("/")}
        >
          Go to Home
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminPrivateRoute;

